/**
 * Bundle Sticky ATC Component
 *
 * A sticky footer button that mirrors the state of the main product-add-to-cart component.
 * Syncs button text, price, disabled state, and acts as a proxy click to the main ATC button.
 */

if (!customElements.get("bundle-sticky-atc")) {
  class BundleStickyAtc extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLButtonElement | null} */
      this.button = null;
      /** @type {HTMLElement | null} */
      this.buttonText = null;
      /** @type {HTMLElement | null} */
      this.buttonPrice = null;
      /** @type {HTMLElement | null} */
      this.message = null;
      /** @type {HTMLElement | null} */
      this.mainAtc = null;
      /** @type {MutationObserver | null} */
      this.observer = null;
      /** @type {(() => void) | null} */
      this.scrollHandler = null;
      /** @type {(() => void) | null} */
      this.resizeHandler = null;
      /** @type {boolean} */
      this.isVisible = false;
    }

    connectedCallback() {
      this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector("[data-sticky-button]"));
      this.buttonText = /** @type {HTMLElement | null} */ (this.querySelector("[data-sticky-button-text]"));
      this.buttonPrice = /** @type {HTMLElement | null} */ (this.querySelector("[data-sticky-button-price]"));
      this.message = /** @type {HTMLElement | null} */ (this.querySelector("[data-sticky-message]"));

      if (this.button) {
        this.button.addEventListener("click", this.handleClick.bind(this));
      }

      // Find main ATC component within the parent bundle-atc-component
      this.findMainAtc(0);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.scrollHandler) {
        window.removeEventListener("scroll", this.scrollHandler);
        this.scrollHandler = null;
      }

      if (this.resizeHandler) {
        window.removeEventListener("resize", this.resizeHandler);
        this.resizeHandler = null;
      }
    }

    /**
     * @param {number} retryCount
     */
    findMainAtc(retryCount) {
      // Look for the main ATC within the bundle-atc-component parent
      const bundleAtcComponent = this.closest("bundle-atc-component");
      if (bundleAtcComponent) {
        this.mainAtc = bundleAtcComponent.querySelector("product-add-to-cart");
      }

      // Fallback: search in document
      if (!this.mainAtc) {
        this.mainAtc = document.querySelector("product-add-to-cart");
      }

      if (this.mainAtc) {
        this.observeMainAtc();
        this.syncState();
        this.setupScrollVisibility();
        // Force initial visibility check
        this.updateVisibility();
      } else if (retryCount < 20) {
        // Retry after a short delay in case main ATC hasn't been rendered yet
        setTimeout(() => {
          this.findMainAtc(retryCount + 1);
        }, 100);
      }
    }

    observeMainAtc() {
      if (!this.mainAtc || this.observer) return;

      // Observe attribute changes on the main ATC component
      this.observer = new MutationObserver(mutations => {
        let shouldSync = false;

        for (const mutation of mutations) {
          if (mutation.type === "attributes" || mutation.type === "childList" || mutation.type === "characterData") {
            shouldSync = true;
            break;
          }
        }

        if (shouldSync) {
          this.syncState();
        }
      });

      this.observer.observe(this.mainAtc, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
        attributeFilter: [
          "data-selected-variant-id",
          "data-price",
          "data-compare-at-price",
          "data-inventory-policy",
          "data-inventory-quantity",
          "data-items",
          "data-sold-out",
        ],
      });

      // Also listen for custom events from the main ATC
      this.mainAtc.addEventListener("statechange", () => this.syncState());
    }

    syncState() {
      if (!this.mainAtc) return;

      const mainButton = /** @type {HTMLButtonElement | null} */ (
        this.mainAtc.querySelector("[data-add-to-cart-button]")
      );
      const mainButtonText = /** @type {HTMLElement | null} */ (this.mainAtc.querySelector("[data-button-text]"));
      const mainButtonPrice = /** @type {HTMLElement | null} */ (this.mainAtc.querySelector("[data-button-price]"));
      const mainOutOfStockMessage = /** @type {HTMLElement | null} */ (
        this.mainAtc.querySelector("[data-out-of-stock-message]")
      );

      if (!mainButton) return;

      // Sync disabled state
      if (this.button) {
        this.button.disabled = mainButton.disabled;
      }

      // Sync button text
      if (this.buttonText && mainButtonText) {
        this.buttonText.textContent = mainButtonText.textContent;
      }

      // Sync button price (HTML for compare prices)
      if (this.buttonPrice && mainButtonPrice) {
        this.buttonPrice.innerHTML = mainButtonPrice.innerHTML;
      }

      // Sync aria-label
      if (this.button && mainButton) {
        const ariaLabel = mainButton.getAttribute("aria-label") || mainButtonText?.textContent || "Add to Cart";
        this.button.setAttribute("aria-label", ariaLabel);
      }

      // Sync button state classes
      this.syncButtonStateClasses(mainButton);

      // Sync out of stock message
      this.syncMessage(mainOutOfStockMessage);
    }

    /**
     * @param {HTMLButtonElement | null} mainButton
     */
    syncButtonStateClasses(mainButton) {
      if (!this.button || !mainButton) return;

      // Extract state class from main button
      const stateClasses = [
        "product__addToCartButton--default",
        "product__addToCartButton--pre-order",
        "product__addToCartButton--subscribe",
        "product__addToCartButton--out-of-stock",
        "product__addToCartButton--sold-out",
      ];

      // Remove all state classes from sticky button
      this.button.classList.remove(
        "bundle-sticky-atc__button--default",
        "bundle-sticky-atc__button--pre-order",
        "bundle-sticky-atc__button--subscribe",
        "bundle-sticky-atc__button--out-of-stock",
        "bundle-sticky-atc__button--sold-out"
      );

      // Find and apply matching state class
      for (const stateClass of stateClasses) {
        if (mainButton.classList.contains(stateClass)) {
          const stickyStateClass = stateClass.replace("product__addToCartButton--", "bundle-sticky-atc__button--");
          this.button.classList.add(stickyStateClass);
          break;
        }
      }
    }

    /**
     * @param {HTMLElement | null} mainOutOfStockMessage
     */
    syncMessage(mainOutOfStockMessage) {
      if (!this.message) return;

      // Check if main button is in out_of_stock state
      const mainButton = /** @type {HTMLButtonElement | null} */ (
        this.mainAtc?.querySelector("[data-add-to-cart-button]")
      );
      const isOutOfStock = mainButton?.classList.contains("product__addToCartButton--out-of-stock");

      if (isOutOfStock && mainOutOfStockMessage && !mainOutOfStockMessage.hidden) {
        this.message.textContent = mainOutOfStockMessage.textContent;
        this.message.hidden = false;
      } else {
        this.message.hidden = true;
      }
    }

    /**
     * @param {Event} event
     */
    handleClick(event) {
      event.preventDefault();

      if (!this.mainAtc || this.button?.disabled) return;

      // Find and click the main ATC button
      const mainButton = /** @type {HTMLButtonElement | null} */ (
        this.mainAtc.querySelector("[data-add-to-cart-button]")
      );
      if (mainButton) {
        mainButton.click();
      }
    }

    setupScrollVisibility() {
      // Show sticky when main ATC button is not visible
      this.scrollHandler = this.throttle(() => {
        this.updateVisibility();
      }, 100);

      this.resizeHandler = this.throttle(() => {
        this.updateVisibility();
      }, 100);

      window.addEventListener("scroll", this.scrollHandler, { passive: true });
      window.addEventListener("resize", this.resizeHandler, { passive: true });

      // Initial check
      this.updateVisibility();
    }

    updateVisibility() {
      // Only show below lg breakpoint (< 1024px)
      if (window.innerWidth >= 1024) {
        this.hide();
        return;
      }

      if (!this.mainAtc) {
        // Show anyway if we haven't found main ATC yet (it might appear later)
        this.show();
        return;
      }

      const mainButton = this.mainAtc.querySelector("[data-add-to-cart-button]");
      if (!mainButton) {
        this.show();
        return;
      }

      const rect = mainButton.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Show sticky when main button is above or below the viewport
      // rect.top >= windowHeight means button is below viewport
      // rect.bottom <= 0 means button is above viewport
      const isMainButtonVisible = rect.top < windowHeight && rect.bottom > 0;

      if (isMainButtonVisible) {
        this.hide();
      } else {
        this.show();
      }
    }

    show() {
      if (!this.isVisible) {
        this.isVisible = true;
        this.classList.add("bundle-sticky-atc--visible");
      }
    }

    hide() {
      if (this.isVisible) {
        this.isVisible = false;
        this.classList.remove("bundle-sticky-atc--visible");
      }
    }

    /**
     * @param {() => void} func
     * @param {number} limit
     * @returns {() => void}
     */
    throttle(func, limit) {
      /** @type {boolean} */
      let inThrottle = false;
      return () => {
        if (!inThrottle) {
          func();
          inThrottle = true;
          setTimeout(() => (inThrottle = false), limit);
        }
      };
    }
  }

  customElements.define("bundle-sticky-atc", BundleStickyAtc);
}
